import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import SwapHoriz from '@mui/icons-material/SwapHoriz'
import Home from '@mui/icons-material/Home'
import Person from '@mui/icons-material/Person'
import Business from '@mui/icons-material/Business'
import LocalHospital from '@mui/icons-material/LocalHospital'
import TvIcon from '@mui/icons-material/Tv'
import OpenInNew from '@mui/icons-material/OpenInNew'
import FormatQuote from '@mui/icons-material/FormatQuote'
import { useNavigate } from 'react-router'
import { styled } from '@mui/material/styles'
import { getUserRoles } from 'src/mentor/layout/utils'
import { PORTAL_CONFIG } from 'src/shared/config/portalConfig'

const FooterContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.6) 0%, rgba(241, 245, 249, 0.8) 100%)',
  backdropFilter: 'blur(12px) saturate(150%)',
  WebkitBackdropFilter: 'blur(12px) saturate(150%)',
  borderTop: '1px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.04)',
  padding: theme.spacing(2, 3),
  minHeight: '72px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: 'auto',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5, 2),
    minHeight: '64px',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.3), transparent)',
  },
}))

const QuoteContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  maxWidth: '60%',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    width: '100%',
    justifyContent: 'center',
    textAlign: 'center',
    gap: theme.spacing(1),
  },
}))

const QuoteIcon = styled(FormatQuote)(({ theme }) => ({
  fontSize: 32,
  color: 'rgba(100, 116, 139, 0.4)',
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    fontSize: 24,
  },
}))

const QuoteText = styled(Typography)(({ theme }) => ({
  color: 'rgba(71, 85, 105, 0.75)',
  fontStyle: 'italic',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.2px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  [theme.breakpoints.down('md')]: {
    fontSize: '13px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
  },
}))

const PortalSwitcherContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    justifyContent: 'center',
  },
}))

const PortalSwitchButton = styled(IconButton)(({ theme }) => ({
  color: 'rgba(71, 85, 105, 0.7)',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  border: '1px solid rgba(226, 232, 240, 0.8)',
  padding: theme.spacing(0.75),
  width: '36px',
  height: '36px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    borderColor: 'rgba(148, 163, 184, 0.6)',
    color: 'rgba(51, 65, 85, 0.9)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
  },
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.down('sm')]: {
    width: '32px',
    height: '32px',
    padding: theme.spacing(0.5),
  },
}))

const PortalMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(67, 56, 202, 0.15)',
    borderRadius: '16px',
    boxShadow: '0 -12px 40px rgba(67, 56, 202, 0.25), 0 4px 12px rgba(0, 0, 0, 0.1)',
    minWidth: '280px',
    maxWidth: '320px',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(0.5, 0),
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      bottom: -6,
      right: 20,
      width: 12,
      height: 12,
      background: 'rgba(255, 255, 255, 0.98)',
      borderRight: '1px solid rgba(67, 56, 202, 0.15)',
      borderBottom: '1px solid rgba(67, 56, 202, 0.15)',
      transform: 'translateY(50%) rotate(45deg)',
      zIndex: 0,
    },
  },
  '& .MuiMenuItem-root': {
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    padding: theme.spacing(1.25, 2),
    borderRadius: '8px',
    margin: theme.spacing(0.25, 1),
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(67, 56, 202, 0.06)',
      transform: 'translateX(2px)',
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '&.active': {
      background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)',
      borderLeft: '3px solid rgba(67, 56, 202, 0.8)',
      fontWeight: 600,
      color: '#4338ca',
      '&:hover': {
        backgroundColor: 'rgba(67, 56, 202, 0.15)',
        transform: 'translateX(0)',
      },
      '& .MuiSvgIcon-root': {
        color: '#4338ca',
      },
    },
  },
}))

// Portal configuration is now imported from centralized config (see imports above)

// Default quotes from Bhagavad Gita
const DEFAULT_QUOTES = [
  "He who heals the suffering of others performs the highest form of Karma Yoga.",
  "To serve the suffering is to serve Me",
]

export default function MentorFooter() {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const userRoles = getUserRoles()
  const [anchorEl, setAnchorEl] = useState(null)
  const [isVisible, setIsVisible] = useState(true)
  const [quote, setQuote] = useState(null)

  const open = Boolean(anchorEl)

  // Get available portals based on user roles
  const availablePortals = PORTAL_CONFIG.filter((portal) =>
    portal.roles.some((role) => userRoles.includes(role)),
  )

  // Always show footer - it's inline in the body
  useEffect(() => {
    setIsVisible(true)
  }, [])

  // Fetch quote from API (placeholder for future implementation)
  useEffect(() => {
    // TODO: Replace with actual API call when ready
    // async function fetchQuote() {
    //   try {
    //     const resp = await getMPData('/api/secure/teacher/footer-quote')
    //     if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
    //       const data = getDataMapFromCommonResponse(resp)
    //       setQuote(data?.quote)
    //     }
    //   } catch (err) {
    //     console.error('Failed to load quote:', err)
    //   }
    // }
    // fetchQuote()

    // For now, use a default quote
    const randomQuote = DEFAULT_QUOTES[Math.floor(Math.random() * DEFAULT_QUOTES.length)]
    setQuote(randomQuote)
  }, [])

  const handlePortalMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePortalMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePortalSwitch = (portal) => {
    handlePortalMenuClose()

    setTimeout(() => {
      if (portal.external) {
        window.open(portal.path, '_blank', 'noopener,noreferrer')
      } else if (portal.path === '/') {
        sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
        sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
        sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        localStorage.removeItem('lastChosenFlow')
        window.location.replace('/')
      } else {
        navigate(portal.path)
      }
    }, 100)
  }

  const isCurrentPortal = (portal) => {
    if (portal.external) return false

    const currentPath = window.location.pathname

    if (portal.path === '/') {
      return (
        currentPath === '/' ||
        (!currentPath.startsWith('/support-home') &&
          !currentPath.startsWith('/mentor-home') &&
          !currentPath.startsWith('/clinic-home') &&
          !currentPath.startsWith('/ca-home') &&
          !currentPath.startsWith('/kiosk') &&
          !currentPath.startsWith('/workplace'))
      )
    }

    return currentPath === portal.path || currentPath.startsWith(portal.path + '/')
  }

  if (!quote) return null

  return (
    <FooterContainer>
        {/* Quote Section */}
        <QuoteContainer>
          <QuoteIcon />
          <QuoteText>{quote}</QuoteText>
        </QuoteContainer>

        {/* Portal Switcher */}
        {availablePortals.length > 1 && (
          <PortalSwitcherContainer>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(100, 116, 139, 0.7)',
                fontWeight: 400,
                fontSize: isMobile ? '11px' : '12px',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Switch Portal:
            </Typography>
            <PortalSwitchButton
              onClick={handlePortalMenuClick}
              aria-label="Switch portal"
              size="small"
            >
              <SwapHoriz sx={{ fontSize: isMobile ? 18 : 20 }} />
            </PortalSwitchButton>

            <PortalMenu
              anchorEl={anchorEl}
              open={open}
              onClose={handlePortalMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: isMobile ? 'center' : 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: isMobile ? 'center' : 'right',
              }}
              PaperProps={{
                sx: {
                  maxHeight: isMobile ? '60vh' : '70vh',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.3)',
                    },
                  },
                },
              }}
            >
              {availablePortals.map((portal) => {
                const Icon = portal.icon
                const isActive = isCurrentPortal(portal)
                const shouldDisable = isActive && !portal.external && portal.path !== '/'
                return (
                  <MenuItem
                    key={portal.id}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handlePortalSwitch(portal)
                    }}
                    className={isActive ? 'active' : ''}
                    disabled={shouldDisable}
                    sx={{
                      '&.active': {
                        '& .portal-icon': {
                          color: '#4338ca',
                        },
                        '& .portal-name': {
                          color: '#4338ca',
                          fontWeight: 600,
                        },
                        '& .portal-badge': {
                          background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                          color: '#4338ca',
                          border: '1px solid rgba(67, 56, 202, 0.3)',
                        },
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5} width="100%">
                      <Icon
                        className="portal-icon"
                        sx={{
                          fontSize: 20,
                          color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.6)',
                          transition: 'color 0.2s ease',
                        }}
                      />
                      <Box flex={1}>
                        <Typography
                          className="portal-name"
                          variant="body2"
                          fontWeight={isActive ? 600 : 500}
                          sx={{
                            color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.87)',
                            transition: 'color 0.2s ease',
                          }}
                        >
                          {portal.name}
                        </Typography>
                      </Box>
                      {isActive && (
                        <Box
                          className="portal-badge"
                          sx={{
                            height: '20px',
                            fontSize: '10px',
                            fontWeight: 600,
                            padding: '0 6px',
                            display: 'flex',
                            alignItems: 'center',
                            background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                            color: '#4338ca',
                            border: '1px solid rgba(67, 56, 202, 0.3)',
                            borderRadius: '10px',
                          }}
                        >
                          Current
                        </Box>
                      )}
                      {portal.external && (
                        <OpenInNew
                          sx={{
                            fontSize: 16,
                            color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.5)',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                )
              })}
            </PortalMenu>
          </PortalSwitcherContainer>
        )}
      </FooterContainer>
  )
}
